/**
 * State Export/Import Component
 * Provides buttons for saving and restoring calculator configurations
 */

import React from 'react';
import { Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface StateExportImportProps {
  onExport: (metadata?: { name?: string }) => void;
  onImport: () => void;
  className?: string;
  variant?: 'default' | 'compact' | 'icon';
}

export const StateExportImport: React.FC<StateExportImportProps> = ({
  onExport,
  onImport,
  className,
  variant = 'default',
}) => {
  if (variant === 'icon') {
    return (
      <div className={cn('flex items-center gap-1', className)}>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onExport()}
          title="Export configuration"
          className="h-8 w-8"
        >
          <Download className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onImport}
          title="Import configuration"
          className="h-8 w-8"
        >
          <Upload className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn('border-border/60 bg-slate-800/50 hover:bg-slate-700/50', className)}
          >
            <Download className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">Export/Import</span>
            <span className="sm:hidden">Save</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onExport()}>
            <Download className="h-4 w-4 mr-2" />
            Export Configuration
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onImport}>
            <Upload className="h-4 w-4 mr-2" />
            Import Configuration
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onExport()}
        className="border-border/60"
      >
        <Download className="h-4 w-4 mr-1.5" />
        Export
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onImport}
        className="border-border/60"
      >
        <Upload className="h-4 w-4 mr-1.5" />
        Import
      </Button>
    </div>
  );
};
